import { CurrencyIcon } from "./shared/CurrencyIcon";
import "./DailyLoginPopup.css";
import { useT } from "../i18n/i18nContext";

interface DailyLoginLadderProps {
  rewards: readonly number[];
  /** The streak day a claim made now lands on. */
  today: number;
  /** True once today's reward has been taken, so today reads as done. */
  claimed: boolean;
}

/**
 * The 10-day streak ladder, today highlighted. Shared by the popup and the Daily Rewards page.
 *
 * Every tile has the same three rows — label, art, amount — so a claimed day and an open one line
 * up. The ✓ is a corner badge, not text after the label: as text it wrapped onto its own line on
 * a phone for some days and not others, and the grid went ragged.
 */
export function DailyLoginLadder({ rewards, today, claimed }: DailyLoginLadderProps) {
  const t = useT();
  const lastDay = rewards.length;
  return (
    <ol className="daily-login__ladder" aria-label={t("progression.ladder.aria")}>
      {rewards.map((amount, i) => {
        const day = i + 1;
        const state = day < today || (day === today && claimed) ? "done" : day === today ? "today" : "later";
        const big = day === lastDay;
        return (
          <li
            key={day}
            className={`daily-login__day daily-login__day--${state}${big ? " daily-login__day--big" : ""}`}
            aria-current={day === today ? "step" : undefined}
            aria-label={t(state === "done" ? "progression.ladder.dayAriaClaimed" : "progression.ladder.dayAria", { day, amount })}
          >
            <span className="daily-login__day-label">{t("progression.ladder.day", { day })}</span>
            {big ? (
              <span className="daily-login__day-gift" aria-hidden="true">🎁</span>
            ) : (
              <CurrencyIcon currency="suika" />
            )}
            <span className="daily-login__day-amount">{amount}</span>
            {state === "done" && (
              <span className="daily-login__day-check" aria-hidden="true">✓</span>
            )}
          </li>
        );
      })}
    </ol>
  );
}
